import * as React from "react";
import { Button, Dropdown, Modal, notification, Switch, Tooltip } from "antd";
import { ChevronDown, RefreshCw } from "react-feather";
import { linksOf, nameOfKind, parentLink, request } from "../../services/cpln";
import { useNavigate, useLocation } from "react-router-dom";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../utils/time";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { IpSet } from "../../schema/types/ipset/entity";
import { useGetIpSetQuery } from "../../store/api/ipSetApi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { dateString } from "../../components/dateStringFn";
import { DateString } from "../../components/dateString";
import { ViewModal } from "../../components/modals/viewModal";
import { CloneModal } from "../../components/modals/cloneModal";
import { DeleteModal } from "../../components/modals/deleteModal";
import { TerraformImportModal } from "../../components/modals/tfImportModal";
import { K8sCrdExportModal } from "../../components/modals/k8sCrdExportModal";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { selectContext } from "../../store/contextSlice";
import { selectLabel } from "../../store/timezoneSlice";
import { Kind } from "../../schema/types/base";
import { LastUpdated } from "../../components/LastUpdated";
import { applyActions } from "../../store/applySlice";

interface Props {
  ipSet: IpSet;
}
export const IpSetDetailHeader: React.FC<Props> = ({ ipSet }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selfLink = linksOf(ipSet).self!;
  const { refetch, isFetching, fulfilledTimeStamp } = useGetIpSetQuery(selfLink, { skip: !selfLink });

  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const { org, gvc } = useAppSelector(selectContext);
  const timezoneLabel = useAppSelector(selectLabel);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [action, setAction] = React.useState<
    "edit" | "view" | "clone" | "delete" | "lock" | "tfImportCommand" | "k8sCrdExport" | ""
  >("");

  const kind: any = ipSet.kind;
  const itemHelpers = itemHelpersBase({ org: org, gvc: gvc, kind: kind, name: ipSet.name });

  function getIsLocked(): boolean {
    return (ipSet.tags || {})["cpln/protected"] == "true";
  }

  const onLock = async () => {
    try {
      const isLocked = getIsLocked();
      await request({ method: "patch", body: { tags: { "cpln/protected": !isLocked } }, url: selfLink });
      notification.success({
        message: "Success",
        description: `Object was ${isLocked ? "unlocked" : "locked"} successfully`,
      });
      refetch();
      setAction("");
    } catch (e) {
      setAction("");
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  };

  const canExportToTerraform = terraformSupportedKinds.includes(kind as Kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind as Kind);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (terraformImportCommand) {
      setAction("tfImportCommand");
    }
  }, [terraformImportCommand]);

  async function cloneGeneric(body: any) {
    await request({ method: "post", url: parentLink(kind), body });
  }

  async function handleActionsClick({ key }: any) {
    switch (key) {
      case "edit":
        editApply();
        break;
      case "view":
        setAction("view");
        break;
      case "export-json":
        await itemHelpers.handleExportJSON([selfLink], false);
        break;
      case "export-yaml":
        await itemHelpers.handleExportYaml([selfLink], false);
        break;
      case "export-json-slim":
        await itemHelpers.handleExportJSON([selfLink], true);
        break;
      case "export-yaml-slim":
        await itemHelpers.handleExportYaml([selfLink], true);
        break;
      case "export-terraform":
        setTerraformImportCommand(await itemHelpers.handleExportTerraform([selfLink]));
        break;
      case "export-k8s-crd":
        setAction("k8sCrdExport");
        break;
      case "clone":
        setAction("clone");
        break;
      case "delete":
        setAction("delete");
        break;
      default:
        break;
    }
  }

  async function editApply() {
    dispatch(applyActions.initialize({ itemLink: selfLink, previousPath: pathname }));
    navigate(`/console/apply`);
  }

  const items: ItemType<MenuItemType>[] = [];
  items.push({ key: "edit", label: "Edit & Apply" });
  items.push({ key: "view", label: "View" });
  const exportItem = {
    key: "ignore2",
    label: "Export",
    children: [
      { key: "export-json", label: "JSON" },
      { key: "export-yaml", label: "YAML" },
      { key: "export-json-slim", label: "JSON Slim" },
      { key: "export-yaml-slim", label: "YAML Slim" },
    ],
  };
  if (canExportToTerraform) {
    exportItem.children.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    exportItem.children.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  items.push(exportItem);
  items.push({ key: "clone", label: "Clone" });
  items.push({ key: "delete", label: "Delete", danger: true });

  function viewModalOnClose() {
    setAction("");
  }

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            {nameOfKind(kind).toUpperCase()}
          </div>
          <div className="text-2xl font-semibold leading-none">{ipSet.name}</div>
          <LastUpdated prefix="Fetched" timestamp={fulfilledTimeStamp} />
        </div>
        <div className="flex-grow" />
        <div className="flex items-center gap-2">
          <div>{`Version ${ipSet.version}`}</div>
          <Button
            disabled={isFetching}
            loading={isFetching}
            onClick={() => refetch()}
            icon={<RefreshCw size={17} />}
            size="small"
            type="default"
          />
          {items.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: items, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(ipSet.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(ipSet.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(ipSet.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(ipSet.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(ipSet.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(ipSet.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={ipSet.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
            <div>
              <Tooltip title={`Click to ${getIsLocked() ? "unlock" : "lock"} the object`} trigger={["hover"]}>
                <Switch
                  value={getIsLocked()}
                  aria-label="Toggle Lock"
                  onChange={() => setAction("lock")}
                  checkedChildren={<LockOutlined />}
                  unCheckedChildren={<UnlockOutlined />}
                />
              </Tooltip>
            </div>
          </>
        </div>
      </div>
      {action === "view" ? (
        <ViewModal
          kind={kind}
          link={selfLink}
          visible={true}
          onClose={viewModalOnClose}
          title={ipSet.name}
          filename={itemHelpers.getFileName()}
        />
      ) : action === "clone" ? (
        <CloneModal clone={cloneGeneric} kind={kind} link={selfLink} name={ipSet.name} onClose={() => setAction("")} />
      ) : action === "delete" ? (
        <DeleteModal
          kind={kind}
          itemName={ipSet.name}
          links={[selfLink]}
          onCancel={() => setAction("")}
          onComplete={() => setAction("")}
        />
      ) : action === "lock" ? (
        <Modal
          title={"Warning!"}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={onLock}
          cancelButtonProps={{ disabled: isFetching }}
          okButtonProps={{ disabled: isFetching, loading: isFetching }}
        >
          <div>
            {getIsLocked()
              ? "Unlocking the object will allow deletion. Are you sure?"
              : "Locking the object will prevent deletion. Are you sure?"}
          </div>
        </Modal>
      ) : action === "tfImportCommand" ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setAction("")} />
      ) : action === "k8sCrdExport" ? (
        <K8sCrdExportModal
          kind={kind}
          links={[selfLink]}
          onClose={() => {
            setAction("");
          }}
        />
      ) : null}
    </>
  );
};
